import { InvitationPasswordActionType } from '../../store/invitation-password/actions';
import { MessageTypes } from '../../constants';
import { BankAccountsActionType } from '../../store/bank-accounts/actions';
import { PaymentLinkActionType } from '../../store/payment-link/actions';
import { AuthActionType } from '../../store/auth/actions';
import { CompanyInformationActionType } from '../../store/company-information/actions';
import { Routes } from '../../config/routes';
import { EmailValidationActionType } from '../../store/email-validation/actions';
import { PasswordResetActionType } from '../../store/password-reset/actions';
import { ProfileActionType } from '../../store/profile/actions';
import { RefundsActionType } from '../../store/refunds/actions';
import { SettlementsActionType } from '../../store/settlements/actions';
import { SignUpActionType } from '../../store/sign-up/actions';
import { TransacionesActionType } from '../../store/transactions/actions';
import { VirtualPosActionType } from '../../store/virtual-pos/actions';
import { PanelStore } from '../../store/interfaces';
import { CheckoutThemesActionType } from '../../store/checkout-theme/actions';
import { PaymentMethodsActionType } from '../../store/payment-methods/actions';
import { SplitPaymentsActionType } from '../../store/split-payments/actions';
import { PayoutsActionType } from '../../store/payouts/actions';
import l from '../../lang';

export const errorFieldsCode = 103;
export const invalidDocumentCode = 201;
export const invalidMinimumAmount = 306;
export const invalidCountryCode = 308;
export const invalidBankAccountCode = 707;
export const defaultMsgGettingData = ' _ ';

/* Payouts */
export const invalidPayoutParam = 2300;
export const amountExceedsAvailableBalance = 2303;
export const errorCreatingPayout = 2306;
export const invalidPayoutRequest = 2307;
export const payoutFieldMaxLengthExceeded = 2314;
export const payoutFieldRegexValidationFailed = 2315;
export const payoutMinimumAmountNotReached = 2319;
export const dailyPayoutLimitExceeded = 2320;
export const payoutMissingRequiredField = 143;
export const twoFactorOtpRateLimited = 222;

export interface ErrorInfo {
  level: MessageTypes;
  code?: number;
  title?: string;
  message?: string;
  messageArgs?: Array<string | number>;
}

const getData = (value: string | number) => {
  if (value) {
    return value;
  }
  return defaultMsgGettingData;
};

type PayoutErrorBody = {
  errorCode?: number;
  errorMessage?: string;
};

const payoutFieldLabelKeys: Record<string, string> = {
  'beneficiary.first_name': 'payouts.create.field.firstName',
  'beneficiary.last_name': 'payouts.create.field.lastName',
  'beneficiary.email': 'payouts.create.field.email',
  purpose: 'payouts.create.field.purpose',
  'beneficiary.bank_account.code': 'payouts.create.field.bankName',
  'beneficiary.bank_account.type': 'payouts.create.field.accountType',
  'beneficiary.bank_account.account': 'payouts.create.field.accountNumber',
  'beneficiary.document.id': 'payouts.create.field.document',
  'beneficiary.document.type': 'payouts.create.field.documentType',
  'beneficiary.bank_account.branch': 'payouts.create.field.bankBranch',
  'beneficiary.address.street': 'payouts.create.field.beneficiaryAddress',
  'beneficiary.business_name': 'payouts.create.field.businessName',
  'beneficiary.address.city': 'payouts.create.field.beneficiaryAddressCity',
  'remitter.first_name': 'payouts.create.field.remitterFirstName',
  'remitter.last_name': 'payouts.create.field.remitterLastName',
  'remitter.document.id': 'payouts.create.field.remitterDocument',
  'remitter.document.type': 'payouts.create.field.documentType',
  'remitter.nationality': 'payouts.create.field.remitterNationality',
  'remitter.birth_info.date': 'payouts.create.field.remitterBirthDate',
};

const normalizePayoutFieldPath = (fieldPath: string) =>
  fieldPath
    .replace(/\.document_id$/, '.document.id')
    .replace(/\.document_type$/, '.document.type')
    .replace(/_/g, '.');

const getPayoutErrorFieldLabel = (rawFieldPath: string) => {
  const normalizedFieldPath = normalizePayoutFieldPath(rawFieldPath);
  const translationKey = payoutFieldLabelKeys[normalizedFieldPath];

  if (translationKey) {
    return l(translationKey);
  }

  const fallback = normalizedFieldPath.split('.').pop() || normalizedFieldPath;
  return fallback.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
};

const getPayoutErrorInfo = (body?: PayoutErrorBody): ErrorInfo | null => {
  const errorCode = body?.errorCode;
  const errorMessage = body?.errorMessage?.toLowerCase() || '';

  if (!errorCode && !errorMessage) {
    return null;
  }

  if (errorCode === errorCreatingPayout) {
    return {
      level: MessageTypes.ERROR,
      message: 'payouts.create.error.internal',
    };
  }

  if (
    errorMessage.includes('payout is not enabled for country') ||
    errorMessage.includes('b2b payout is not enabled for country')
  ) {
    return {
      level: MessageTypes.ERROR,
      message: 'payouts.create.error.countryCurrencyNotEnabled',
    };
  }

  if (errorMessage.includes('missing required field:')) {
    const rawFieldPath = body?.errorMessage?.split(':').slice(1).join(':').trim() || '';
    return {
      level: MessageTypes.ERROR,
      message: 'payouts.create.error.missingRequiredField',
      messageArgs: [getPayoutErrorFieldLabel(rawFieldPath)],
    };
  }

  if (errorMessage.includes('beneficiary.document.')) {
    return {
      code: invalidDocumentCode,
      level: MessageTypes.ERROR,
      message: 'msgError.companyInformation.document',
    };
  }

  if (errorMessage.includes('beneficiary.bank_account.account')) {
    return {
      code: invalidBankAccountCode,
      level: MessageTypes.ERROR,
      message: 'msgError.addBeneficiary.bankAccountNumber',
    };
  }

  if (errorMessage.includes('beneficiary.bank_account.branch')) {
    return {
      level: MessageTypes.ERROR,
      message: 'msgError.addBeneficiary.bankAccountBranch',
    };
  }

  if (errorMessage.includes('beneficiary.bank_account.type')) {
    return {
      level: MessageTypes.ERROR,
      message: 'msgError.addBeneficiary.bankAccountType',
    };
  }

  if (body?.errorMessage) {
    return {
      level: MessageTypes.ERROR,
      message: 'payouts.create.error.providerRejected',
      messageArgs: [body.errorMessage],
    };
  }

  return {
    level: MessageTypes.ERROR,
    message: 'payouts.create.error.unexpected',
  };
};

export const errorCodes = (state?: PanelStore.RootState, body?: { errorCode?: number; errorMessage?: string }) => {
  return {
    GENERIC_ERROR: {
      level: MessageTypes.ERROR,
      title: 'genericError.title',
      message: 'genericError.message',
    },
    [AuthActionType.LOGIN_ERROR]: {
      105: {
        level: MessageTypes.ERROR,
        title: 'msgError.login.block.headline',
        message: 'msgError.login.block.subheadline',
        messageArgs: ['window.openWidgetHelp()'],
        align: 'center',
      },
      202: {
        level: MessageTypes.ERROR,
        title: 'msgError.login.wrong.password.headline',
        message: 'msgError.login.wrong.password.subheadline',
        align: 'center',
      },
      209: {
        level: MessageTypes.ERROR,
        message: 'msgError.login.emailNotVerified',
      },
      default: {
        level: MessageTypes.ERROR,
        message: 'msgError.login.default',
      },
    },
    [AuthActionType.TWO_FACTOR_VERIFY_ERROR]: {
      221: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.expiredChallenge',
      },
      218: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.notEnabled',
      },
      219: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.notConfigured',
      },
      220: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.invalidCode',
      },
      [twoFactorOtpRateLimited]: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.rateLimited',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      default: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.generic',
      },
    },
    [SignUpActionType.SIGN_UP_ERROR]: {
      204: {
        level: MessageTypes.ERROR,
        message: 'msgError.signUp.emailAlreadyRegistered',
      },
    },

    [InvitationPasswordActionType.CONFIRM_INVITATION_PASSWORD_ERROR]: {
      216: {
        level: MessageTypes.ERROR,
        message: 'msgError.invitationPassword.alreadyUsed',
      },
      default: {
        level: MessageTypes.ERROR,
        message: 'genericError.message',
      },
    },
    [CompanyInformationActionType.SEND_KYC_ERROR]: {
      [errorFieldsCode]: {
        code: errorFieldsCode,
      },
      [invalidDocumentCode]: {
        level: MessageTypes.ERROR,
        title: 'msgError.companyInformation.invalidDocument.headline',
        message: 'msgError.companyInformation.invalidDocument.subheadline',
        inputError: { name: 'document', message: 'companyInformation.kyc.invalid' },
      },
      307: {
        level: MessageTypes.ERROR,
        title: 'msgError.companyInformation.kycTwo.fraudReview.headline',
        message: 'msgError.companyInformation.kycTwo.fraudReview.subheadline',
      },
      309: {
        level: MessageTypes.ERROR,
        message: 'msgError.companyInformation.countryIndicated',
      },
      319: {
        level: MessageTypes.ERROR,
        title: 'msgError.companyInformation.documentAlreadyInUse.headline',
        message: 'msgError.companyInformation.documentAlreadyInUse.subheadline',
        inputError: { name: 'document', message: 'companyInformation.kyc.documentAlreadyInUse' },
      },
      320: {
        level: MessageTypes.ERROR,
        title: 'msgError.companyInformation.websiteAlreadyInUse.headline',
        message: 'msgError.companyInformation.websiteAlreadyInUse.subheadline',
        inputError: { name: 'website', message: 'companyInformation.kyc.websiteAlreadyInUse' },
      },
      327: {
        level: MessageTypes.ERROR,
        title: 'msgError.companyInformation.websiteInvalid.headline',
        message: 'msgError.companyInformation.websiteInvalid.subheadline',
        inputError: { name: 'website', message: 'companyInformation.kyc.websiteInvalid' },
      },
    },
    [EmailValidationActionType.CONFIRM_EMAIL_ERROR]: {
      203: {
        level: MessageTypes.ERROR,
        message: 'msgError.emailValidation.invalidEmail',
      },
      205: {
        level: MessageTypes.ERROR,
        message: 'msgError.emailValidation.tokenExpired',
      },
    },
    [SettlementsActionType.REQUEST_SETTLEMENT_ERROR]: {
      703: {
        level: MessageTypes.ERROR,
        message: 'msgError.settlementInProcess',
      },
      704: {
        level: MessageTypes.ERROR,
        message: 'msgError.amountExceedsBalance',
      },
      705: {
        level: MessageTypes.ERROR,
        message: 'msgError.paymentProcessorError',
      },
    },
    [BankAccountsActionType.ADD_BANK_ACCOUNT_ERROR]: {
      103: {
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.invalidData',
      },
      201: {
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.document',
      },
      701: {
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.countryIndicated',
      },
      [invalidBankAccountCode]: {
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.bankAccountNumber',
      },
      708: {
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.bankAccountBranch',
      },
      709: {
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.bankAccountType',
      },
      712: {
        level: MessageTypes.ERROR,
        title: 'msgError.addBeneficiary.alreadyExists.headline',
        message: 'msgError.addBeneficiary.alreadyExists.subheadline',
      },
    },
    [BankAccountsActionType.DELETE_BANK_ACCOUNT_ERROR]: {
      718: {
        level: MessageTypes.ERROR,
        message: 'bankAccounts.delete.error.718',
      },
    },
    [VirtualPosActionType.CREATE_PAYMENT_ERROR]: {
      [errorFieldsCode]: {
        code: errorFieldsCode,
      },
      903: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.currencyNotSupported',
      },
      303: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.transactionLimitReached',
      },
      304: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.billingLimitReached',
      },
      305: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.transactionAmountExceeded',
        messageArgs: [
          getData(state?.dashboard?.merchantMe?.kycLevel?.maxTransactionAmount || 0),
          getData(state?.dashboard?.merchantMe?.kycLevel?.maxTransactionAmountCc || 0),
          Routes.COMPANY_INFORMATION,
        ],
      },
      306: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.amountLessThanAllowed',
      },
      307: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.accountIsNotOperational',
      },
      308: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.noPaymentMethodConfigured',
      },
      316: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.amountExceedsAllowedForCards',
      },
      900: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.clientEmailNotEntered',
      },
      1602: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.duplicatedClientId',
      },
    },
    [VirtualPosActionType.CONFIRM_PAYMENT_ERROR]: {
      [errorFieldsCode]: {
        code: errorFieldsCode,
      },
      [invalidDocumentCode]: {
        code: invalidDocumentCode,
      },
    },
    [PaymentLinkActionType.CREATE_PAYMENT_LINK_ERROR]: {
      [errorFieldsCode]: {
        code: errorFieldsCode,
      },
      [invalidDocumentCode]: {
        code: invalidDocumentCode,
      },
      903: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.currencyNotSupported',
      },
      904: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.invoiceAlreadyExists',
      },
      303: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.transactionLimitReached',
      },
      304: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.billingLimitReached',
      },
      305: {
        level: MessageTypes.WARNING,
        message: 'msgError.createPaymentLink.transactionAmountExceeded',
        messageArgs: [
          getData(state?.dashboard?.merchantMe?.kycLevel?.maxTransactionAmount || 0),
          Routes.COMPANY_INFORMATION,
        ],
      },
      [invalidMinimumAmount]: {
        code: invalidMinimumAmount,
        message: 'msgError.createPaymentLink.amountLessThanAllowed',
      },
      307: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.accountIsNotOperational',
      },
      [invalidCountryCode]: {
        code: invalidCountryCode,
        message: 'msgError.createPaymentLink.noPaymentMethodConfigured',
      },
      316: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.amountExceedsAllowedForCards',
      },
      900: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.clientEmailNotEntered',
      },
      1602: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.duplicatedClientId',
      },
    },
    [RefundsActionType.REQUEST_REFUND_ERROR]: {
      [invalidBankAccountCode]: {
        code: invalidBankAccountCode,
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.bankAccountNumber',
      },
      601: {
        level: MessageTypes.ERROR,
        message: 'msgError.requestRefund.dataProvided',
      },
      605: {
        level: MessageTypes.ERROR,
        message: 'msgError.requestRefund.amountEntered',
      },
      606: {
        level: MessageTypes.ERROR,
        message: 'msgError.requestRefund.insufficientBalance',
      },
      607: {
        level: MessageTypes.ERROR,
        message: 'msgError.requestRefund.amountLess',
      },
      608: {
        level: MessageTypes.ERROR,
        message: 'msgError.requestRefund.chargebackInPlace',
      },
      846: {
        level: MessageTypes.ERROR,
        message: 'msgError.requestRefund.chargebackInPlace',
      },
    },
    [PasswordResetActionType.SEND_PASSWORD_RESET_ERROR]: {
      206: {
        level: MessageTypes.ERROR,
        message: 'msgError.passwordReset.waitToResend',
      },
    },
    [PasswordResetActionType.CONFIRM_PASSWORD_RESET_ERROR]: {
      205: {
        level: MessageTypes.ERROR,
        message: 'msgError.passwordReset.invalidOrExpired',
      },
    },
    [ProfileActionType.CHANGE_PASSWORD_ERROR]: {
      202: {
        level: MessageTypes.ERROR,
        message: 'msgError.changePassword',
      },
    },
    [ProfileActionType.TWO_FACTOR_SETUP_ERROR]: {
      217: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.alreadyEnabled',
      },
      default: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.generic',
      },
    },
    [ProfileActionType.TWO_FACTOR_CONFIRM_ERROR]: {
      219: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.notConfigured',
      },
      220: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.invalidCode',
      },
      [twoFactorOtpRateLimited]: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.rateLimited',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      default: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.generic',
      },
    },
    [ProfileActionType.TWO_FACTOR_DISABLE_ERROR]: {
      218: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.notEnabled',
      },
      219: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.notConfigured',
      },
      220: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.invalidCode',
      },
      [twoFactorOtpRateLimited]: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.rateLimited',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      default: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.generic',
      },
    },
    [TransacionesActionType.GET_PAYMENT_METHODS_FOR_TRANSACTIONS_ERROR]: {
      default: {
        level: MessageTypes.ERROR,
        message: 'genericError.message',
      },
    },
    [RefundsActionType.GET_PAYMENT_METHODS_FOR_REFUNDS_ERROR]: {
      default: {
        level: MessageTypes.ERROR,
        message: 'genericError.message',
      },
    },
    [CheckoutThemesActionType.TOGGLE_CHECKOUT_THEME_ERROR]: {
      2101: {
        title: 'msgError.checkoutThemes.not_approved.title',
        level: MessageTypes.ERROR,
        message: 'msgError.checkoutThemes.not_approved',
      },
    },
    [PaymentMethodsActionType.CALCULATE_ERROR]: {
      306: {
        level: MessageTypes.ERROR,
        message: 'msgError.createPaymentLink.amountLessThanAllowed',
      },
    },
    [SplitPaymentsActionType.VALIDATE_MERCHANT_COLLABORATOR_ERROR]: {
      203: {
        level: MessageTypes.WARNING,
        message: 'msgError.splitPayments.collaboratorEmailInvalid',
      },
    },
    [PayoutsActionType.VALIDATE_PARTIAL_PAYOUT_ERROR]: {
      [invalidDocumentCode]: {
        code: invalidDocumentCode,
        level: MessageTypes.ERROR,
        message: 'msgError.companyInformation.document',
      },
      [invalidBankAccountCode]: {
        code: invalidBankAccountCode,
        level: MessageTypes.ERROR,
        message: 'msgError.addBeneficiary.bankAccountNumber',
      },
      [invalidPayoutParam]: getPayoutErrorInfo(body) || {
        level: MessageTypes.ERROR,
        message: 'payouts.create.error.invalidPayoutParam',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      [invalidPayoutRequest]: getPayoutErrorInfo(body) || {
        level: MessageTypes.ERROR,
        message: 'payouts.create.error.validationFailed',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      default: {
        ...(getPayoutErrorInfo(body) || {
          level: MessageTypes.ERROR,
          message: 'payouts.create.error.validationFailed',
          messageArgs: [getData(body?.errorMessage || '')],
        }),
      },
    },
    [PayoutsActionType.CONFIRM_PAYOUT_ERROR]: {
      221: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.expiredChallenge',
      },
      218: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.notEnabled',
      },
      219: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.notConfigured',
      },
      220: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.invalidCode',
      },
      [twoFactorOtpRateLimited]: {
        level: MessageTypes.ERROR,
        message: 'twoFactor.error.rateLimited',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      [invalidPayoutParam]: {
        ...(getPayoutErrorInfo(body) || {
          level: MessageTypes.ERROR,
          message: 'payouts.create.error.invalidPayoutParam',
          messageArgs: [getData(body?.errorMessage || '')],
        }),
      },
      [invalidPayoutRequest]: {
        ...(getPayoutErrorInfo(body) || {
          level: MessageTypes.ERROR,
          message: 'payouts.create.error.validationFailed',
          messageArgs: [getData(body?.errorMessage || '')],
        }),
      },
      [dailyPayoutLimitExceeded]: {
        level: MessageTypes.ERROR,
        message: 'payouts.create.error.dailyPayoutLimitExceeded',
      },
      [amountExceedsAvailableBalance]: {
        level: MessageTypes.ERROR,
        message: 'payouts.create.error.amountExceedsAvailableBalance',
      },
      [payoutFieldMaxLengthExceeded]: {
        level: MessageTypes.ERROR,
        message: 'payouts.create.error.payoutFieldMaxLengthExceeded',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      [payoutFieldRegexValidationFailed]: {
        level: MessageTypes.ERROR,
        message: 'payouts.create.error.payoutFieldRegexValidationFailed',
        messageArgs: [getData(body?.errorMessage || '')],
      },
      [payoutMinimumAmountNotReached]: {
        level: MessageTypes.ERROR,
        message: 'payouts.create.error.payoutMinimumAmountNotReached',
      },
      [errorFieldsCode]: {
        ...(getPayoutErrorInfo(body) || {
          level: MessageTypes.ERROR,
          message: 'payouts.create.error.validationFailed',
          messageArgs: [getData(body?.errorMessage || '')],
        }),
      },
      [payoutMissingRequiredField]: {
        ...(getPayoutErrorInfo(body) || {
          level: MessageTypes.ERROR,
          message: 'payouts.create.error.validationFailed',
          messageArgs: [getData(body?.errorMessage || '')],
        }),
      },
      default: {
        ...(getPayoutErrorInfo(body) || {
          level: MessageTypes.ERROR,
          message: 'payouts.create.error.validationFailed',
          messageArgs: [getData(body?.errorMessage || '')],
        }),
      },
    },
  };
};
